import * as React from 'react';
import { IServerUserInfo } from '../../ts/models/IServerUserInfo';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { UserSettingsNotificationParams, UserSettingsNotificationType, UserSettingsNotification } from '../notifications/UserSettingsNotification';
import { getAppStore } from '../../app/AppStore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { FormikHelpers, FormikProps, Formik } from 'formik';
import { UserSettings } from '../services/resources/UserSettings';
import { ListItem, List, DropdownButtonType } from '@tableau/widgets';
import { UserSettingsStyle } from '../styles/UserSettingsStyle';
import { FormikSingleSelectDropdown } from '../../commonInfra/components/FormikSingleSelectDropdown';
import { style } from 'typestyle';
import { InlineCancelAndSaveButtons } from './UserSettingEditButtons';
import { Messages } from '../../codegen/Localize';
import { LocalesInstance } from '../../ts/resources/Locales';
import { ILocale } from '../../ts/ServerApi/types/ILocale';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';

export interface LocaleFormFields {
  locale: string;
}

export type LocaleFormProps = {
  initialValues: LocaleFormFields;
  serverUser: IServerUserInfo;
}

const userIsViewingTheirOwnSettings = (serverService: SessionManagementService, serverUser: IServerUserInfo) => {
  return serverService.sessionUsername() === serverUser.username
    && serverService.sessionUserDomainName() === serverUser.domainName;
};

const notifyLocaleChange = (result: IResult, username: string) => {
  const userSettingsNotificationParams: UserSettingsNotificationParams = {
    result: result,
    username: username,
    userSettingsNotificationType: UserSettingsNotificationType.ChangeLocale
  };
  UserSettingsNotification.notify(userSettingsNotificationParams);
};

async function showLocaleNotification(result: IResult, serverUser: IServerUserInfo) {
  const serverService = getSessionManagementServiceInstance();
  if (result && userIsViewingTheirOwnSettings(serverService, serverUser)) {
    await serverService.refreshSessionInfo();
    getAppStore().dispatch(incrementRouterSpaceReactKey());
    notifyLocaleChange(result, serverUser.username);
  } else if (result) {
    notifyLocaleChange(result, serverUser.username);
  }
}

function showLocaleButton(formik: FormikProps<LocaleFormFields>, initialLocale: string): boolean {
  if (!formik.dirty || !formik.isValid || formik.isSubmitting || (formik.values.locale.trim() === initialLocale)) {
    return false;
  }
  return true;
}

const unspecifiedLocaleItemValue = 'UnspecifiedLocale';
export function LocaleForm(props: LocaleFormProps): JSX.Element {
  const [items, setItems] = React.useState([]);
  const [usersCurrentLocale, setUsersCurrentLocale] = React.useState('');

  React.useEffect(() => {
    async function fetchLocales() {
      let locales: ILocale[] = await LocalesInstance.getAll();
      locales.unshift(LocalesInstance.unspecifiedLocale());
      const newItems: ListItem[] = locales.map((locale: ILocale) => ({
        content: locale.displayName,
        value: locale.locale ? locale.locale : unspecifiedLocaleItemValue,
        testId: locale.locale ? locale.locale : unspecifiedLocaleItemValue
      }));
      const usersCurrentLocale = props.initialValues.locale ? props.initialValues.locale : unspecifiedLocaleItemValue;
      setUsersCurrentLocale(usersCurrentLocale);
      setItems(newItems);
    }
    if (items.length === 0) {
      fetchLocales();
    }
  });

  const handleSubmit = React.useCallback(async (locale: string, formik: FormikHelpers<LocaleFormFields>) => {
    if (locale !== usersCurrentLocale) {
      let localeToUpdate = locale;
      if (locale === unspecifiedLocaleItemValue) {
        localeToUpdate = null;
      }
      const result: IResult = await UserSettings.updateUserLocale(props.serverUser.id, localeToUpdate);
      showLocaleNotification(result, props.serverUser);
      if (!result || result.errors) {
        formik.resetForm();
      } else {
        setUsersCurrentLocale(locale);
      }
  }}, [props, setUsersCurrentLocale]);

  return (
    <Formik
      initialValues={{ locale: usersCurrentLocale }}
      onSubmit={function(values: LocaleFormFields, formik: FormikHelpers<LocaleFormFields>) {
        handleSubmit(values.locale, formik);
      }}
      enableReinitialize={true}
    >
      {(formik: FormikProps<LocaleFormFields>) => (
        <form onSubmit={formik.handleSubmit}>
          <table>
            <tbody className={UserSettingsStyle.tbodyNew}>
              {/* Locale Section */}
              <tr data-test-id="locale-section">
              <td className={UserSettingsStyle.labelTextNew}>
                  {Messages.locale()}
              </td>
              <td className={UserSettingsStyle.detailsValue}>
                  <FormikSingleSelectDropdown
                      name="locale"
                      list={<List items={items} className={UserSettingsStyle.dropdownList}/>}
                      type={DropdownButtonType.Outline}
                      label={''}
                      className={UserSettingsStyle.dropdown}/>
              </td>
              </tr>
              {(showLocaleButton(formik, usersCurrentLocale) &&
                <InlineCancelAndSaveButtons
                testId={'edit-locale'}
                formik={formik}
                />
              )}
            </tbody>
          </table>
          <div>
          </div>
        </form>
      )}
    </Formik>
  );
}
